/**
 * SUNTRONIX ONE — Tenant Branding admin.
 *
 * Suntronix operators can upload a per-tenant logo (renders on the login
 * recognition card) and toggle the tenant's passkey policy — no
 * engineering ticket required.
 */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Fingerprint, Loader2, Image as ImageIcon, CheckCircle2 } from "lucide-react";

export default function TenantBranding() {
  const [companies, setCompanies] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/master-companies");
      setCompanies(data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    if (!sel) return;
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 512 * 1024) {
      toast.error("Logo must be ≤ 512 KiB");
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(f);
      });
      await api.post(`/master-companies/${sel.id}/logo`,
                          { data_url: dataUrl });
      toast.success("Tenant logo uploaded");
      load();
      setSel({ ...sel, logo_url: dataUrl });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    finally { setUploading(false); e.target.value = ""; }
  };

  const togglePasskey = async () => {
    if (!sel) return;
    try {
      await api.post(`/master-companies/${sel.id}/policy`,
                          { passkey_enabled: !sel.passkey_enabled });
      toast.success(
        sel.passkey_enabled
          ? "Passkey login disabled for this tenant"
          : "Passkey login enabled for this tenant");
      setSel({ ...sel, passkey_enabled: !sel.passkey_enabled });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-[1200px] space-y-4" data-testid="tenant-branding-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-[#667085]">
          Administration · Tenants · Branding
        </div>
        <h1 className="text-2xl font-heading font-semibold text-[#101828] tracking-tight">
          Tenant branding &amp; policy
        </h1>
        <p className="text-[13px] text-[#667085] mt-1 max-w-3xl">
          Upload the tenant logo for the login recognition card and control
          the passkey policy — no engineering ticket needed.  Logos are
          limited to 512 KiB (PNG · JPEG · WebP · SVG).
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[320px_minmax(0,1fr)] gap-4">
        {/* Tenant list */}
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-[0.14em] text-[#667085]">
            Tenants ({companies.length})
          </div>
          <ul className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
            {busy && companies.length === 0 ? (
              <li className="px-3 py-6 text-center text-[#98A2B3]">
                <Loader2 className="h-4 w-4 mx-auto animate-spin" />
              </li>
            ) : companies.map((c) => (
              <li key={c.id}>
                <button onClick={() => setSel(c)}
                          data-testid={`tenant-pick-${c.id}`}
                          className={`w-full text-left px-3 py-2 text-[13px] flex items-center gap-2 hover:bg-slate-50 ${
                              sel?.id === c.id ? "bg-[#EFF5FF]" : ""}`}>
                  <span className="h-6 w-6 rounded bg-white border border-slate-200 grid place-items-center overflow-hidden">
                    {c.logo_url ? (
                      <img src={c.logo_url} alt="" className="max-h-5 max-w-5 object-contain" />
                    ) : (
                      <span className="text-[10px] text-[#667085]">{(c.name || "?")[0]}</span>
                    )}
                  </span>
                  <span className="truncate flex-1 text-[#101828]">{c.name}</span>
                  {c.passkey_enabled && <Fingerprint className="h-3 w-3 text-[#2962FF]" />}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Detail */}
        <div className="bg-white border border-slate-200 rounded-md p-5">
          {!sel ? (
            <div className="text-center text-[#98A2B3] py-16">
              <ImageIcon className="h-6 w-6 mx-auto mb-2 opacity-50" />
              Pick a tenant on the left to manage branding.
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <div className="text-[10px] tracking-[0.16em] uppercase text-[#667085]">Tenant</div>
                <div className="text-[18px] font-heading text-[#101828] tracking-tight">
                  {sel.name}
                </div>
                <div className="text-[11px] text-[#98A2B3] font-mono">{sel.id}</div>
              </div>

              {/* Logo section */}
              <div className="border-t border-slate-200 pt-4">
                <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-[#0D1B3D]">
                  Login recognition logo
                </div>
                <div className="mt-3 flex items-center gap-4">
                  <div className="h-20 w-20 rounded-lg border border-slate-200 bg-white grid place-items-center overflow-hidden">
                    {sel.logo_url ? (
                      <img src={sel.logo_url} alt={`${sel.name} logo`}
                            className="max-h-16 max-w-16 object-contain"
                            data-testid="tenant-branding-preview" />
                    ) : (
                      <ImageIcon className="h-6 w-6 text-[#98A2B3]" />
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="inline-flex items-center gap-2 rounded-md bg-brand-blue hover:bg-brand-blueh text-white text-[13px] font-medium h-10 px-4 cursor-pointer"
                            data-testid="tenant-branding-upload">
                      {uploading
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <Upload className="h-4 w-4" />}
                      Upload logo
                      <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                              onChange={onFile}
                              className="hidden" />
                    </label>
                    <div className="text-[11px] text-[#667085] mt-2 leading-relaxed">
                      PNG · JPEG · WebP · SVG · ≤ 512 KiB. Preferably square
                      or 3:1 rectangle at 240px minimum.
                    </div>
                  </div>
                </div>
              </div>

              {/* Passkey policy */}
              <div className="border-t border-slate-200 pt-4">
                <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-[#0D1B3D]">
                  Authentication policy
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <button onClick={togglePasskey}
                            data-testid="tenant-branding-passkey"
                            className={`inline-flex items-center gap-2 rounded-md border h-10 px-4 text-[13px] font-medium transition-colors ${
                                sel.passkey_enabled
                                    ? "border-[#2962FF] bg-[#EFF5FF] text-[#2962FF]"
                                    : "border-slate-200 text-[#667085] hover:bg-slate-50"}`}>
                    <Fingerprint className="h-4 w-4" />
                    {sel.passkey_enabled ? "Passkey enabled" : "Passkey disabled"}
                    {sel.passkey_enabled && <CheckCircle2 className="h-3 w-3" />}
                  </button>
                  <div className="text-[11px] text-[#667085] max-w-md leading-relaxed">
                    When enabled, users of this tenant see a prominent
                    <b> Continue with passkey</b> button on the sign-in screen.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
